import { useSelector } from 'react-redux'
import { Input, Radio } from 'antd'
import {
  faAt,
  faBirthdayCake,
  faGenderless,
  faGlobe,
  faGraduationCap,
  faIdBadge,
  faLanguage,
  faListNumeric,
  faPhone,
  faUser
} from '@fortawesome/pro-solid-svg-icons'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import FormItem from '../../../Components/shared/Forms/FormItem'
import { EUROPEAN_DATE } from '../../../utils/momentjs'
import BirthdatePicker from '../../../Components/antd/Selects/BirthdatePicker'
import { Genders } from '../../../utils/api/student'
import { Student, StudentProperties } from '../../../utils/entities/student'
import { Permissions } from './SearchStudentModalProvider'
import Mailing from '../../../Components/shared/Mailing'
import LocaleLanguagesSelector from '../../../Components/shared/Selectors/Languages/LanguageSelector'
import SchoolSectionsSelector from '../../../Components/shared/Selectors/SchoolSectionsSelector'
import NationalitiesSelector from '../../../Components/shared/Selectors/NationalitiesSelector'
import { getIdOrNull } from '../../../utils'

const FIELD_WIDTH = '100%'
const FIELD_STYLE = { width: FIELD_WIDTH }

const { FIRSTNAME, LASTNAME, EMAIL, GENDER, BIRTHDATE, MATRICULE, NATIONALITY, PHONE_NUMBER, LANGUAGE, NISS, SECTIONS } = StudentProperties

export const Types = Object.freeze({
  INTERNSHIP_REQUEST: 'internship_request',
  STUDENT: 'student'
})

const StudentForm = ({ errorFields = [], data, languages, permission, type = Types.INTERNSHIP_REQUEST, onChange }) => {
  const t = useSelector(selectTranslate)

  const hasPermission = fieldValue => (
    permission === Permissions.GOD ||
    ((permission === Permissions.WRITE || permission === Permissions.FILL) && fieldValue == null)
  )

  const handleChange = key => value => onChange(new Student({ ...data, [key]: value }))
  const handleEventChange = key => e => onChange(new Student({ ...data, [key]: e.target.value }))
  const handleLanguageChange = languageTag => onChange(new Student({ ...data, language: languageTag }))

  const className = field => errorFields.includes(field) ? 'error-ant-border' : ''

  const required = permission !== Permissions.READ_ONLY

  return (
    <>
      {type === Types.STUDENT && (
        <FormItem icon={faGraduationCap} label='form.label.section' className={className(SECTIONS)} required>
          <SchoolSectionsSelector
            style={{ width: '100%' }}
            values={getIdOrNull(data.sections[0])}
            onChange={section => onChange(new Student({ ...data, sections: [section] }))}
          />
        </FormItem>
      )}
      {type === Types.STUDENT && (
        <FormItem icon={faListNumeric} label='form.label.matricule' className={className(MATRICULE)}>
          <Input
            className={className(MATRICULE)}
            style={FIELD_STYLE}
            value={data[MATRICULE]}
            onChange={handleEventChange(MATRICULE)}
          />
        </FormItem>
      )}
      {type === Types.STUDENT && (
        <FormItem icon={faGlobe} label='form.label.nationality' className={className(NATIONALITY)}>
          <NationalitiesSelector
            style={{ width: '100%' }}
            values={data[NATIONALITY]}
            onChange={nationality => onChange(new Student({ ...data, nationality: nationality }))}
          />
        </FormItem>
      )}
      <FormItem icon={faGenderless} label='form.label.gender' className={className(GENDER)}>
        <Radio.Group value={data[GENDER]} buttonStyle='solid' onChange={handleEventChange(GENDER)} disabled={!hasPermission(GENDER)}>
          {Object.keys(Genders).map(k => (
            <Radio.Button key={k} value={Genders[k]}> {t('genders.' + k.toLowerCase())} </Radio.Button>
          ))}
        </Radio.Group>
      </FormItem>
      <div className='flex-row w-100'>
        <FormItem className='w-50 pdr-4' icon={faUser} label='form.label.firstname' required={required}>
          <Input
            className={className(FIRSTNAME)}
            style={FIELD_STYLE}
            value={data[FIRSTNAME]}
            onChange={handleEventChange(FIRSTNAME)}
            disabled={!hasPermission(FIRSTNAME)}
          />
        </FormItem>
        <FormItem className='w-50 pdl-4' icon={faUser} label='form.label.lastname' required={required}>
          <Input
            className={className(LASTNAME)}
            style={FIELD_STYLE}
            value={data[LASTNAME]}
            onChange={handleEventChange(LASTNAME)}
            disabled={!hasPermission(LASTNAME)}
          />
        </FormItem>
      </div>
      <FormItem icon={faIdBadge} label='form.label.niss' info='form.label.niss_info'>
        <Input
          className={className(StudentProperties.NISS)}
          style={FIELD_STYLE}
          value={data[NISS]}
          onChange={handleEventChange(StudentProperties.NISS)}
          disabled={!hasPermission(StudentProperties.NISS)}
        />
      </FormItem>
      <FormItem icon={faAt} label='form.label.school_email' required={required}>
        {permission === Permissions.READ_ONLY ? (
          <div className='pdl-8 fake-gray-input w-100'>
            <Mailing email={data[EMAIL]} />
          </div>
        ) : (
          <Input
            className={className(EMAIL)}
            style={FIELD_STYLE}
            value={data[EMAIL]}
            onChange={handleEventChange(EMAIL)}
            disabled={!hasPermission(EMAIL)}
          />
        )}
      </FormItem>
      <div className='flex-row w-100'>
        <FormItem className={'w-50 pdr-4 ' + className(LANGUAGE)} icon={faLanguage} label='form.label.language' info={t('internship_request_from.info.language')} required={required}>
          <LocaleLanguagesSelector
            disabled={!hasPermission(LANGUAGE)}
            languages={languages}
            style={FIELD_STYLE}
            values={data[LANGUAGE]}
            onChange={handleLanguageChange}
          />
        </FormItem>
        <FormItem className='w-50 pdl-4' icon={faPhone} label='form.label.phone_number'>
          <Input value={data[PHONE_NUMBER]} onChange={handleEventChange(PHONE_NUMBER)} disabled={!hasPermission(PHONE_NUMBER)} />
        </FormItem>
      </div>
      <FormItem icon={faBirthdayCake} label='form.label.birthdate'>
        <BirthdatePicker value={data[BIRTHDATE]?.format(EUROPEAN_DATE)} onChange={handleChange(BIRTHDATE)} disabled={!hasPermission(BIRTHDATE)} />
      </FormItem>
    </>
  )
}

export default StudentForm
